import { Zap } from 'lucide-react';
import { ScanViewfinderControls, DOC_TYPES } from './ScanViewfinderControls';
import { ScanGuideFrame } from './ScanGuideFame';
import type { DocType } from './types';
import type { CameraStatus } from '@/mobile/hooks/useCameraStream';

interface ScanViewfinderProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  cameraStatus: CameraStatus;
  docType: DocType;
  onDocTypeChange: (dt: DocType) => void;
  onCapture: () => void;
  onGallery: () => void;
  onClose: () => void;
}

/**
 * Tela de viewfinder com câmera ao vivo.
 * Feed de vídeo + moldura-guia animada + controles.
 */
export function ScanViewfinder({
  videoRef,
  canvasRef,
  cameraStatus,
  docType,
  onDocTypeChange,
  onCapture,
  onGallery,
  onClose,
}: ScanViewfinderProps) {
  const cameraReady = cameraStatus === 'ready';
  const typeLabel = DOC_TYPES.find((d) => d.id === docType)?.label ?? 'documento';

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: '#0b0d0c', overflow: 'hidden' }}>
      {/* Feed câmera */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: cameraReady ? 'block' : 'none' }}
      />

      {/* Canvas oculto para captura */}
      <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />

      {/* Placeholder câmera indisponível */}
      {!cameraReady && (
        <div
          aria-live="polite"
          style={{
            position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
            background: 'repeating-linear-gradient(45deg, #14171a, #14171a 12px, #181c20 12px, #181c20 24px)',
            color: 'rgba(255,255,255,0.35)',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', marginTop: 10, maxWidth: 200, lineHeight: 1.5 }}>
              {cameraStatus === 'starting' ? 'Iniciando câmera…' : 'Aguardando permissão de câmera'}
            </div>
          </div>
        </div>
      )}

      {/* Gradiente */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.50) 0%, transparent 22% 68%, rgba(0,0,0,0.60) 100%)',
        }}
      />

      {/* Topo: fechar + flash */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 54px)',
          left: 0, right: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0 18px', zIndex: 10,
        }}
      >
        <button onClick={onClose} aria-label="Fechar scanner" style={iconBtnStyle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <button aria-label="Flash (indisponível)" disabled style={{ ...iconBtnStyle, opacity: 0.4, cursor: 'not-allowed' }}>
          <Zap size={18} strokeWidth={1.7} aria-hidden />
        </button>
      </div>

      {/* Hint */}
      <div
        aria-live="polite"
        style={{
          position: 'absolute', top: '15%', left: 0, right: 0,
          textAlign: 'center', color: '#fff', padding: '0 30px',
          pointerEvents: 'none', zIndex: 5,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>Centralize o {typeLabel.toLowerCase()} na moldura</div>
        <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 3 }}>mantenha o celular firme e bem iluminado</div>
      </div>

      <ScanGuideFrame />

      <ScanViewfinderControls
        docType={docType}
        onDocTypeChange={onDocTypeChange}
        onCapture={onCapture}
        onGallery={onGallery}
      />
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  width: 44, height: 44, borderRadius: '50%',
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.2)',
  color: '#fff', display: 'grid', placeItems: 'center',
  cursor: 'pointer', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
